import {
  Box,
  VStack,
  HStack,
  Flex,
  Text,
  Heading,
  Icon,
  Button,
  Badge,
  Skeleton,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  Tooltip,
  useToast,
  useBreakpointValue,
  SimpleGrid,
  Collapse,
  useDisclosure
} from '@chakra-ui/react';
import {
  BellRinging,
  Bell,
  ChatCircleDots,
  Clock,
  DotsThree,
  CheckCircle,
  CaretDown,
  CaretUp,
  Eye,
  MapPin,
  User
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { updateComment } from '../../firebase/siteDiscussions';

export default function NotificationsSection({ notifications, onNotificationUpdate }) {
  const { isOpen, onToggle } = useDisclosure();
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const itemsPerPage = 6;
  const navigate = useNavigate();
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });

  // Calculate total notifications
  const totalNotifications =
    (notifications.siteComments?.length || 0) + 
    (notifications.commentReplies?.length || 0);
  
  const unreadCount = 
    (notifications.siteComments?.filter(n => !n.read)?.length || 0) + 
    (notifications.commentReplies?.filter(n => !n.read)?.length || 0);

  // Sort and paginate notifications
  const allNotifications = [
    ...(notifications.siteComments || []), 
    ...(notifications.commentReplies || [])
  ].sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  
  const paginatedNotifications = allNotifications.slice(0, page * itemsPerPage);
  
  useEffect(() => {
    if (paginatedNotifications.length >= allNotifications.length) {
      setHasMore(false);
    }
  }, [paginatedNotifications.length, allNotifications.length]);

  const handleLoadMore = () => {
    setPage(prev => prev + 1);
  };

  const handleMarkAsRead = async (notification) => {
    try {
      const result = await updateComment(notification.siteId, notification.id, {
        read: true
      });
      
      if (result.success) {
        // Update local state through the parent component
        if (onNotificationUpdate) {
          onNotificationUpdate({
            ...notification,
            read: true
          });
        }
      } else {
        toast({
          title: "Error",
          description: "Failed to mark notification as read",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
      toast({
        title: "Error",
        description: "Failed to mark notification as read",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleMarkAllAsRead = async () => {
    const unreadNotifications = allNotifications.filter(n => !n.read);
    
    if (unreadNotifications.length === 0) {
      toast({
        title: "No unread notifications",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const promises = unreadNotifications.map(notification => 
        updateComment(notification.siteId, notification.id, { read: true })
      );
      
      const results = await Promise.all(promises);
      
      if (results.every(result => result.success)) {
        // Update local state
        if (onNotificationUpdate) {
          unreadNotifications.forEach(notification => {
            onNotificationUpdate({
              ...notification,
              read: true
            });
          });
        }

        toast({
          title: "Success",
          description: `Marked ${unreadNotifications.length} notifications as read`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to mark some notifications as read",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      toast({
        title: "Error",
        description: "Failed to mark notifications as read",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box variant="dashboardSection" p={0}>
      <VStack align="stretch" spacing={0}>
        {/* Collapsible Header */}
        <Flex
          p={6}
          cursor="pointer"
          onClick={onToggle}
          _hover={{ bg: 'bg.accent' }}
          transition="background-color 0.2s"
          justify="space-between"
          align="center"
        >
          <HStack spacing={3}>
            <Flex
              w="40px"
              h="40px"
              bg="bg.accent"
              color="brand.primary"
              borderRadius="md"
              justify="center"
              align="center"
              position="relative"
            >
              <Icon as={BellRinging} weight="duotone" fontSize="xl" />
              {unreadCount > 0 && (
                <Badge
                  position="absolute"
                  top="-2px"
                  right="-2px"
                  variant="solid"
                  colorScheme="red"
                  borderRadius="full"
                  fontSize="xs"
                  px={1.5}
                >
                  {unreadCount}
                </Badge>
              )}
            </Flex>
            <VStack align="start" spacing={1}>
              <Heading size="lg" fontFamily="heading" color="text.default">
                Notifications
              </Heading>
              <HStack spacing={2}>
                <Badge variant="subtle" colorScheme="gray">
                  {totalNotifications} total
                </Badge>
                {unreadCount > 0 && (
                  <Badge variant="subtle" colorScheme="red">
                    {unreadCount} unread
                  </Badge>
                )}
                <Text fontSize="sm" color="text.muted">
                  Click to {isOpen ? 'hide' : 'view'} notifications
                </Text>
              </HStack>
            </VStack>
          </HStack>
          
          <HStack spacing={3}>
            {unreadCount > 0 && (
              <Button
                size="md"
                variant="journal"
                leftIcon={<CheckCircle weight="bold" />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleMarkAllAsRead();
                }}
              >
                Mark All Read
              </Button>
            )}
            <IconButton
              icon={<Icon as={isOpen ? CaretUp : CaretDown} weight="bold" />}
              variant="ghost"
              size="md"
              aria-label={isOpen ? "Hide notifications" : "Show notifications"}
              color="text.muted"
            />
          </HStack>
        </Flex>

        {/* Collapsible Content */}
        <Collapse in={isOpen} animateOpacity>
          <VStack align="stretch" spacing={6} px={6} pb={6}>
            {notifications.loading ? (
              <VStack spacing={4}>
                {[1, 2, 3].map((i) => (
                  <Skeleton
                    key={i}
                    height="120px"
                    width="100%"
                    borderRadius="md"
                    startColor="bg.surface"
                    endColor="bg.accent"
                  />
                ))}
              </VStack>
            ) : totalNotifications === 0 ? (
              <Flex
                direction="column"
                align="center"
                justify="center"
                py={12}
                bg="bg.accent"
                borderRadius="md"
              >
                <Icon as={Bell} fontSize="4xl" color="text.muted" mb={4} />
                <Heading size="md" color="text.default" mb={2}>
                  No notifications yet
                </Heading>
                <Text color="text.muted" textAlign="center">
                  You'll receive notifications when others interact with your sites
                </Text>
              </Flex>
            ) : (
              <>
                {/* Notification Cards Grid */}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  {paginatedNotifications.map((item) => {
                    const isComment = notifications.siteComments?.includes(item);
                    return (
                      <Box
                        key={item.id}
                        p={4}
                        borderWidth="1px"
                        borderRadius="md"
                        position="relative"
                        _hover={{
                          borderColor: "brand.primary",
                          transform: 'translateY(-1px)',
                          boxShadow: 'md'
                        }}
                        transition="all 0.2s"
                        cursor="pointer"
                        bg={item.read ? 'bg.surface' : 'bg.accent'}
                        borderColor={item.read ? 'border.light' : 'brand.primary'}
                        onClick={(e) => {
                          if (e.defaultPrevented) return;
                          navigate(`/site/${item.siteId}#comment-${item.id}`);
                        }}
                      >
                        {!item.read && (
                          <Badge
                            position="absolute"
                            top={3}
                            right={3}
                            variant="solid"
                            colorScheme="red"
                            fontSize="xs"
                          >
                            New
                          </Badge>
                        )}
                        
                        <VStack align="start" spacing={3}>
                          <HStack spacing={3} w="full">
                            <Flex
                              w="32px"
                              h="32px"
                              bg={isComment ? 'blue.100' : 'green.100'}
                              color={isComment ? 'blue.600' : 'green.600'}
                              borderRadius="full"
                              justify="center"
                              align="center"
                              flexShrink={0}
                            >
                              <Icon as={isComment ? MapPin : User} weight="fill" fontSize="md" />
                            </Flex>
                            <VStack align="start" spacing={1} flex={1}>
                              <Text fontSize="sm" fontWeight="semibold" color="text.default">
                                {item.authorName}
                              </Text>
                              <Text fontSize="sm" color="text.muted">
                                {isComment ? "Commented on your site" : "Replied to your comment"}
                              </Text>
                            </VStack>
                          </HStack>
                          
                          {item.siteName && (
                            <Text fontSize="sm" color="text.muted" fontStyle="italic">
                              "{item.siteName}"
                            </Text>
                          )}
                          
                          <HStack w="full" justify="space-between" align="center">
                            <HStack spacing={2}>
                              <Icon as={Clock} fontSize="xs" color="text.muted" />
                              <Text fontSize="xs" color="text.muted">
                                {item.createdAt?.seconds ?
                                  new Date(item.createdAt.seconds * 1000).toLocaleDateString() :
                                  new Date().toLocaleDateString()}
                              </Text>
                            </HStack>
                            
                            {!item.read && (
                              <Tooltip label="Mark as read">
                                <IconButton
                                  icon={<Icon as={Eye} weight="bold" />}
                                  variant="ghost"
                                  size="sm"
                                  color="brand.primary"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleMarkAsRead(item);
                                  }}
                                  aria-label="Mark as read"
                                />
                              </Tooltip>
                            )}
                          </HStack>
                        </VStack>
                      </Box>
                    );
                  })}
                </SimpleGrid>

                {/* Load More Button */}
                {hasMore && (
                  <Flex justify="center" mt={4}>
                    <Button
                      variant="journal"
                      size="md"
                      onClick={handleLoadMore}
                      isLoading={notifications.loading}
                    >
                      Load More Notifications
                    </Button>
                  </Flex>
                )}
              </>
            )}
          </VStack>
        </Collapse>
      </VStack>
    </Box>
  );
}
